import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import offerPageURL from 'lib/offer/offerPageURL'
import { getSuggestedDates } from 'selectors/offerSelectors'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { buildSearchParamsFromFilters, buildSuggestedDatesParamsKey, convertFlexibleDatesToTravelDates, encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { SOURCE_TYPES } from 'constants/source'
import { queryKeySource, urlHashDatePicker, queryKeyAdults, queryKeyChildren, queryKeyDuration, queryKeyFareClass, queryUtmContent, queryUtmSource, queryUtmMedium, queryUtmCampaign, queryKeyPriceTotal, queryKeyCurrency } from 'constants/url'
import { fareClassToUrlMap } from 'constants/flight'
import type { GhaPriceMatchClickThrough } from 'lib/metasearch/ghaPriceMatch'
import useOfferMetaData from './useOfferMetaData'
import { OfferUnavailableReason } from 'lib/search/constants'
import { isLEHotel } from 'lib/offer/offerTypes'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'

interface Options {
  filters?: App.OfferListFilters
  bundledOfferId?: string
  offerLinkIncludesFilters?: boolean
  searchId?: string
  showDatePicker?: boolean
  includeGhaPrice?: boolean
  priceCheck?: string
  utmBp?: string
  utmContent?: string
  /**
   * Forward the remaining core UTMs alongside `utmContent`. The server treats any request
   * carrying a core UTM as a fresh campaign touch and replaces the whole `utmsV2` cookie
   * with just the keys present (`processUtmParameters`), so forwarding `utm_content` alone
   * wipes campaign/source/medium mid-session. Always pass these together with `utmContent`.
   */
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  ruleId?: string
  gpid?: string
  accountType?: string
  priceTotal?: string
  currency?: string
  /** The room and property Google advertised, forwarded so the destination can price-match too. */
  ghaAdvertisedRoom?: Omit<GhaPriceMatchClickThrough, 'token' | 'accountType'>
  /**
   * Google's displayed price breakdown, forwarded verbatim as one set so the offer page verifies
   * against the same figures as the landing it was reached from — see `ghaDisplayedPriceQueryParams`.
   */
  ghaDisplayedPrice?: Record<string, string>
}

function useOfferUrl(offer: App.AnyOffer, options: Options): string
function useOfferUrl(offer: App.AnyOffer | undefined, options: Options): string | undefined
function useOfferUrl(offer: App.AnyOffer | undefined, options: Options): string | undefined {
  const offerId = offer?.id ?? ''
  const { filters, bundledOfferId, offerLinkIncludesFilters, showDatePicker } = options
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(filters?.flexibleMonths, filters?.flexibleNights, filters?.rooms)
  const suggestedDates = useAppSelector(state => getSuggestedDates(state, flexibleSearchFilterKey, offerId))
  const searchMetadata = useOfferMetaData(offerId, options.filters)
  const maldivesBundleContext = useMaldivesDirectBundleContext()
  const departureAirportCodeForUrl = maldivesBundleContext.departureAirportCode ?? filters?.departureAirportCode
  const filtersForUrl = useMemo<App.OfferListFilters | undefined>(() => {
    if (!departureAirportCodeForUrl) return filters
    if (filters?.departureAirportCode === departureAirportCodeForUrl) return filters
    return {
      ...(filters ?? {}),
      departureAirportCode: departureAirportCodeForUrl,
    }
  }, [filters, departureAirportCodeForUrl])

  const isPropertySearchTile = useMemo(() => {
    return (isLEHotel(offer) && filters?.propertyId && !filters?.searchNearby && offer.property?.id === filters.propertyId)
  }, [filters, offer])

  const overrideOccupancy: Array<App.SearchOccupancy> = useMemo(() => searchMetadata?.rooms.map(room => room.occupancy) ?? [], [searchMetadata])

  return useMemo(() => {
    if (!offer) return undefined

    let offerUrlSuggestedDates: App.OfferSuggestedDates | undefined = suggestedDates
    if (!suggestedDates?.checkIn && !suggestedDates?.checkOut && filters?.flexibleNights && filters?.flexibleMonths) {
      const { checkIn, checkOut } = convertFlexibleDatesToTravelDates(filters.flexibleNights as FLEXIBLE_DURATION_RANGE, filters.flexibleMonths)
      offerUrlSuggestedDates = {
        ...suggestedDates,
        checkIn,
        checkOut,
      }
    }

    const isCharterBundle = filtersForUrl?.charterBundle
    const charterBundleHasNoDates = isCharterBundle && (!filtersForUrl?.checkIn || !filtersForUrl?.checkOut || !maldivesBundleContext.hasUserSelectedDates)
    const noDatesInUrl =
      charterBundleHasNoDates ||
      ((!!isPropertySearchTile && searchMetadata?.unavailableReason === OfferUnavailableReason.NUM_NIGHTS_UNSUPPORTED) || showDatePicker)

    const searchParams = offerLinkIncludesFilters ? buildSearchParamsFromFilters(filtersForUrl, offerUrlSuggestedDates, { noDatesInUrl }) : new URLSearchParams()

    if (maldivesBundleContext.charterBundle && maldivesBundleContext.duration) {
      searchParams.set(queryKeyDuration, String(maldivesBundleContext.duration))
    }

    // Persist the full fare class (including reward variant) when navigating from the
    // Maldives Direct search page to an offer page.
    if (maldivesBundleContext.charterBundle) {
      const fc = maldivesBundleContext.selectedFareClass ?? maldivesBundleContext.fareClass
      if (fc) {
        searchParams.set(queryKeyFareClass, fareClassToUrlMap[fc])
      }
      if (maldivesBundleContext.selectedCabin) {
        searchParams.set('selectedCabin', maldivesBundleContext.selectedCabin)
      }
      if (maldivesBundleContext.rewardSeatsOnly) {
        searchParams.set('societeRewards', 'true')
      }
    }

    if (bundledOfferId) {
      searchParams.set('bundledOfferId', bundledOfferId)
    }

    const departureMonths = filtersForUrl?.departureMonths?.filter(Boolean) ?? []
    if (departureMonths.length > 0) {
      departureMonths.forEach(month => {
        searchParams.append('departureMonths', month)
      })
    }

    // `source` may contain multiple source types, like `guestSearch,search,metasearch`. In such
    // cases, metasearch-related rate source types take priority over guest-related source types.
    const isMetasearchSource = !!filtersForUrl?.source?.split(',').includes(SOURCE_TYPES.METASEARCH)
    if (offer.type === OFFER_TYPE_BED_BANK && isMetasearchSource) {
      searchParams.set(queryKeySource, SOURCE_TYPES.METASEARCH)
    }

    if (options.searchId) {
      searchParams.set('searchId', options.searchId)
    }

    if (options.utmBp) {
      searchParams.set('utm_bp', options.utmBp)
    }

    if (options.utmContent) {
      searchParams.set(queryUtmContent, options.utmContent)
    }

    if (options.utmSource) {
      searchParams.set(queryUtmSource, options.utmSource)
    }

    if (options.utmMedium) {
      searchParams.set(queryUtmMedium, options.utmMedium)
    }

    if (options.utmCampaign) {
      searchParams.set(queryUtmCampaign, options.utmCampaign)
    }

    if (options.includeGhaPrice) {
      searchParams.set('gha_price', '1')
    }

    // Forward the priceCheck param onto the /partner page only when it already exists (e.g. GHA
    // price scraper / Hotel Center price-check sessions). Real customer redirects never carry it.
    if (options.priceCheck) {
      searchParams.set('priceCheck', options.priceCheck)
    }

    if (options.ruleId) {
      searchParams.set('ruleId', options.ruleId)
    }

    if (options.gpid) {
      searchParams.set('gpid', options.gpid)
    }

    if (options.accountType) {
      searchParams.set('accountType', options.accountType)
    }

    if (options.ghaAdvertisedRoom) {
      searchParams.set('ghaRoomId', options.ghaAdvertisedRoom.roomId)
      searchParams.set('ghaRefundable', String(options.ghaAdvertisedRoom.refundable))
      searchParams.set('ghaPropertyId', options.ghaAdvertisedRoom.propertyId)
      if (options.ghaAdvertisedRoom.googleCurrency) {
        searchParams.set('ghaCurrency', options.ghaAdvertisedRoom.googleCurrency)
      }
    }

    // Carry Google's cached price to the /partner page for price-accuracy tracking.
    if (options.priceTotal) {
      searchParams.set(queryKeyPriceTotal, options.priceTotal)
    }

    if (options.currency) {
      searchParams.set(queryKeyCurrency, options.currency)
    }

    if (options.ghaDisplayedPrice) {
      Object.entries(options.ghaDisplayedPrice).forEach(([key, value]) => searchParams.set(key, value))
    }

    // Skip occupancy override for charter bundles - preserve user's selected occupancy
    // Use metadata occupancy whenever we have metadata rooms (they represent what the search actually returned)
    const shouldOverrideOccupancy = !isCharterBundle && overrideOccupancy.length > 0

    if (shouldOverrideOccupancy) {
      const adults = overrideOccupancy.map(room => room.numberOfAdults).join(',')

      const children = overrideOccupancy.map(room => encodeChildrenOccupancy(room.agesOfChildren)).join(',')

      // Set the occupancy params
      searchParams.set(queryKeyAdults, adults)
      searchParams.set(queryKeyChildren, children)
    }

    return offerPageURL(offer, searchParams, showDatePicker ? urlHashDatePicker : undefined)
  }, [
    suggestedDates,
    filters,
    filtersForUrl,
    isPropertySearchTile,
    searchMetadata?.unavailableReason,
    offerLinkIncludesFilters,
    bundledOfferId,
    offer,
    options.searchId,
    showDatePicker,
    options.includeGhaPrice,
    options.priceCheck,
    options.utmBp,
    options.utmContent,
    options.utmSource,
    options.utmMedium,
    options.utmCampaign,
    options.ruleId,
    options.gpid,
    options.accountType,
    options.ghaAdvertisedRoom,
    options.priceTotal,
    options.currency,
    options.ghaDisplayedPrice,
    overrideOccupancy,
    maldivesBundleContext.duration,
    maldivesBundleContext.charterBundle,
    maldivesBundleContext.fareClass,
    maldivesBundleContext.selectedFareClass,
    maldivesBundleContext.selectedCabin,
    maldivesBundleContext.rewardSeatsOnly,
    maldivesBundleContext.hasUserSelectedDates,
  ])
}

export default useOfferUrl
